import { Component, OnInit, ViewChild } from '@angular/core';
import { combineLatest, concatMap, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { WhiteLabelService } from '@app/core/services/white-label/white-label.service';
import { AuthenticatedUser } from '@app/shared/models/authenticated-user.model';
import { AppMenu } from '@app/shared/models/nav-item.model';
import { SelectedAccountStore } from '@app/core/stores/selected-account-store.service';
import { ENDPOINTS } from '@app/core/constants/endpoints';
import { CompanyAccount } from '@app/shared/models/company-account.model';
import { AppMenuBuilderService } from '../services/app-menu-builder.service';
import { WindowRefService } from '@app/core/services/helpers/window-ref.service';
import { MatSidenav } from '@angular/material/sidenav';
import { DeviceType } from '@app/shared/enum/device-type';
import { HelpService } from '@core/services/help.service';

interface UserAuthenticatedData {
    user: AuthenticatedUser;
    selectedAccount: CompanyAccount;
    menu: AppMenu;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false,
})
export class HeaderComponent implements OnInit {
    @ViewChild('sidenav') sidenav: MatSidenav;

    isAuthenticated = false;
    logo$: Observable<string>;
    authenticatedData$: Observable<UserAuthenticatedData>;
    dashboardLink = ENDPOINTS.urls.dashboard.base;
    deviceTypes = DeviceType;

    private window: Window;

    constructor(
        private authenticationService: AuthenticationService,
        private whiteLabelService: WhiteLabelService,
        private selectedAccountStore: SelectedAccountStore,
        private appMenuBuilderService: AppMenuBuilderService,
        private windowRefService: WindowRefService,
        private helpService: HelpService
    ) {
        this.window = this.windowRefService.window;
    }

    ngOnInit(): void {
        this.isAuthenticated = this.authenticationService.isAuthenticated();
        this.loadLogo();
        this.loadUserDetails();
    }

    search(value: string): void {
        if (value) {
            this.window.location.href = `${ENDPOINTS.urls.search}#?keyword=${value}`;
        }
    }

    toggleSidenav(): void {
        this.sidenav.toggle().catch(() => {});
    }

    toggleHelpPanel(): void {
        this.helpService.togglePanelVisibility();
    }

    private loadUserDetails(): void {
        this.authenticatedData$ = combineLatest([
            this.selectedAccountStore.selectedAccount$.pipe(filter((account) => !!account)),
            this.authenticationService.user$.pipe(filter((user) => !!user)),
        ]).pipe(
            map(([account, user]) => ({
                user,
                selectedAccount: account,
                menu: this.appMenuBuilderService.createAppMenu(user, account),
            }))
        );
    }

    private loadLogo(): void {
        this.logo$ = this.whiteLabelService.domainInfo$.pipe(
            first((domainInfo) => !!domainInfo),
            concatMap((domain) => {
                return this.whiteLabelService.getProductLightLogo(domain);
            })
        );
    }
}
